/**
 * TalentAlign — TypeScript type contract.
 *
 * Mirrors `packages/engine/src/talent_align_engine/types.py` field-for-field.
 * When you add a signal there, add it here. Runtime validation lives in
 * `packages/shared-types/typescript/` via Zod schemas.
 */

export const Verdict = {
  STRONG: "strong",
  PARTIAL: "partial",
  WEAK: "weak",
} as const;
export type Verdict = (typeof Verdict)[keyof typeof Verdict];

export const SignalName = {
  SEMANTIC: "semantic",
  KEYWORD: "keyword",
  BULLET: "bullet",
  SECTION: "section",
  TITLE: "title",
  SENIORITY: "seniority",
  FIELD: "field",
} as const;
export type SignalName = (typeof SignalName)[keyof typeof SignalName];

export interface Locator {
  section: string;
  bulletIndex?: number | null;
  text: string;
}

export interface Bullet {
  section: string;
  index: number;
  text: string;
  embedding?: number[] | null;
}

export interface ParsedDocument {
  rawText: string;
  sections: Record<string, string>;
  bullets: Bullet[];
  entities?: Record<string, string[]>;
  metadata?: Record<string, unknown>;
}

export interface Resume extends ParsedDocument {
  displayName?: string | null;
}

export interface JobDescription extends ParsedDocument {
  title?: string | null;
}

export interface SubScores {
  semantic: number;
  keyword: number;
  bullet: number;
  section: number;
  title: number;
  seniority: number;
  field: number;
}

export interface Evidence {
  kind: "strength" | "gap" | "keyword" | "transfer";
  jdLocator: Locator | null;
  resumeLocator: Locator | null;
  weight: number;
  note?: string;
}

export interface Match {
  overallScore: number; // 0-100
  verdict: Verdict;
  subScores: SubScores;
  evidence: Evidence[];
  rationale?: string;
  modelUsed?: string;
  promptVersion?: string;
  piiMasked?: boolean;
  latencyMs?: number;
  /** Day 9: cross-encoder rerank score in [0, 1] if the MsMarcoReranker
   *  was invoked. Present only when rerank ran — UI code should check
   *  for undefined before rendering rerank-specific elements. */
  crossEncoderScore?: number;
  /** Bi-encoder score before rerank influence was blended in. Preserved
   *  so the UI can show "bi-encoder said X, cross-encoder said Y". */
  biencoderScore?: number;
}
